import * as styles from "./CountryField.css";

import { useAtomValue, useSetAtom } from "jotai";
import { type JSX, useEffect } from "react";

import {
  Combobox,
  ComboboxContent,
  ComboboxIcon,
  ComboboxInput,
  ComboboxTrigger,
  ComboboxValue,
  ComboboxVirtualizedList,
  useComboboxVirtualizedScroll,
} from "@frontend/components/atoms/Combobox";
import { useLingui } from "@lingui/react/macro";

import { useAddressText } from "../hooks/useAddressText";
import { countriesAtom, loadCountriesAtom } from "../stores";
import type { AddressFieldProps } from "../types";
import type { AddressCountry } from "../types";
import { CountryFlag } from "./CountryFlag";
import { FieldFrame } from "./FieldFrame";

/** The country, held as the ISO2 code the address stores and shown by name. */
export function CountryField({
  field,
  label,
  placeholder,
  size,
  disabled,
}: AddressFieldProps): JSX.Element {
  const { t } = useLingui();

  const { value, setValue, error } = useAddressText(field);

  const countries = useAtomValue(countriesAtom);
  const loadCountries = useSetAtom(loadCountriesAtom);

  const { virtualizerRef, onItemHighlighted } =
    useComboboxVirtualizedScroll<AddressCountry>();

  useEffect(() => {
    void loadCountries();
  }, [loadCountries]);

  const selected = countries.find((country) => country.iso2 === value) ?? null;

  return (
    <FieldFrame label={label} error={error}>
      <Combobox
        virtualized
        items={countries}
        value={selected}
        size={size}
        disabled={disabled}
        itemToStringLabel={(country: AddressCountry | null) =>
          country?.name ?? ""
        }
        isItemEqualToValue={(a: AddressCountry, b: AddressCountry) =>
          a.iso2 === b.iso2
        }
        onItemHighlighted={onItemHighlighted}
        onValueChange={(country: AddressCountry | null) =>
          setValue(country?.iso2 ?? "")
        }
      >
        <ComboboxTrigger>
          <ComboboxValue placeholder={placeholder ?? t`Country`}>
            {(country: AddressCountry | null) =>
              country ? (
                <span className={styles.item}>
                  <CountryFlag
                    iso2={country.iso2}
                    className={styles.itemFlag}
                  />

                  <span className={styles.itemLabel}>{country.name}</span>
                </span>
              ) : null
            }
          </ComboboxValue>

          <ComboboxIcon />
        </ComboboxTrigger>

        <ComboboxContent
          input={
            <ComboboxInput
              placeholder={t`Search...`}
              clearable={false}
              showTrigger={false}
            />
          }
        >
          <ComboboxVirtualizedList<AddressCountry>
            estimateSize={() => 36}
            itemKey={(country) => country.iso2}
            virtualizerRef={virtualizerRef}
          >
            {(country) => (
              <div className={styles.item}>
                <CountryFlag iso2={country.iso2} className={styles.itemFlag} />

                <span className={styles.itemLabel}>{country.name}</span>
              </div>
            )}
          </ComboboxVirtualizedList>
        </ComboboxContent>
      </Combobox>
    </FieldFrame>
  );
}
